import { AirflowCalendarEntry } from '../../types';
import { COLOR_STOPS } from './constants';

export type CalendarCellCounts = { total: number; failed: number };

/*
 * Buckets calendar entries into a day-hour cell map; for daily granularity, hour is forced to 0
 */
export function buildCellMap(entries: AirflowCalendarEntry[], granularity: 'daily' | 'hourly'): Map<string, CalendarCellCounts> {
    const map = new Map<string, CalendarCellCounts>();
    entries.forEach((entry) => {
        const date = new Date(entry.date);
        if (Number.isNaN(date.getTime())) return;
        const day = date.getDate();
        const hour = granularity === 'daily' ? 0 : date.getHours();
        const key = `${day}-${hour}`;
        const existing = map.get(key) ?? { total: 0, failed: 0 };
        existing.total += 1;
        if (entry.state === 'failed') existing.failed += 1;
        map.set(key, existing);
    });
    return map;
}

/*
 * Maps a cell's count (relative to the month's max) into one of 5 color-ramp stops,
 * returning undefined (transparent) at count 0
 */
export function heatColor(count: number, maxCount: number, mode: 'total' | 'failed'): string | undefined {
    if (count <= 0) return undefined;
    const stops = COLOR_STOPS[mode];
    if (maxCount <= 0) return stops[0];
    const ratio = count / maxCount;
    const index = Math.min(stops.length - 1, Math.floor(ratio * stops.length));
    return stops[index];
}

/*
 * Computes the Monday-indexed weekday (0=Mon..6=Sun) and week-of-month index for a given day
 */
export function dayPosition(year: number, month: number, day: number): { weekday: number; week: number } {
    const firstWeekday = (new Date(year, month, 1).getDay() + 6) % 7;
    const offset = firstWeekday + day - 1;
    return { weekday: offset % 7, week: Math.floor(offset / 7) };
}
